import { FormEvent, ReactElement, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { SiteFeature } from '../../../types/siteSettings';
import { useSiteSettings } from '../../../utils/contexts/SiteSettingsContext';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { getPostLoginActions, isAuthorized, savePostLoginActions } from '../../../utils/logIn';
import { Button } from '../../Button/Button';
import { FeatureToggle } from '../../FeatureToggle/FeatureToggle';
import { useBasket } from '../BasketContext/useBasket';
import Location from '../ShippingOutside/Location';
import BasketProducts from './BasketProducts';
import { CouponCodes } from './CouponCodes';
import ShippingMessage from './ShippingMessage';

import styles from '../Basket.module.css';

type CheckoutProps = {
  token: string;
  className: string;
};

const Checkout = ({ token, className }: CheckoutProps): ReactElement | null => {
  const { basket, openMyAccountView, toggleBasket } = useBasket();
  const { sfcc } = useSiteSettings();
  const formRef = useRef<HTMLFormElement>(null);

  const { t } = useFallbackTranslation();

  const checkIfLoggedIn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    isAuthorized() ? event.currentTarget.submit() : openMyAccountView();
  };

  useEffect(() => {
    const goToCheckout = async () => {
      if (isAuthorized() && formRef.current) {
        toggleBasket();
        formRef.current?.submit();
      }
    };
    const [postLoginAction, ...otherActions] = getPostLoginActions();

    if (postLoginAction?.type === 'checkout') {
      savePostLoginActions(otherActions);
      if (!basket?.product_items?.length) {
        return;
      }
      goToCheckout();
    }
  }, [toggleBasket, basket, formRef]);

  const {
    order_price_adjustments = [],
    c_product_sub_total_formatted = '',
    c_product_before_discount_formatted = '',
    c_product_total_formatted = '',
  } = basket ?? {};

  const hasPriceAdjustments = order_price_adjustments.length > 0;

  const regularTotalPrice = hasPriceAdjustments
    ? c_product_sub_total_formatted
    : c_product_before_discount_formatted;

  const showRegularPrice = regularTotalPrice !== c_product_total_formatted;

  return (
    <div className={classNames('flex h-full flex-1 flex-col overflow-auto p-6', className)}>
      <div className="space-between flex flex-1 flex-col">
        <FeatureToggle featureName={SiteFeature.SfccBasketLocationWarning}>
          <Location />
        </FeatureToggle>
        <FeatureToggle featureName={SiteFeature.SfccBasketShippingMessage}>
          <ShippingMessage />
        </FeatureToggle>
        <BasketProducts />
      </div>
      <div className={styles.promoOrReference}>
        <CouponCodes />
      </div>
      <div className={classNames(styles.bottomSection, styles.fat)}>
        <div className="flex w-full flex-col">
          {hasPriceAdjustments && (
            <p className="space-between mb-4 flex flex-1 flex-row border-b border-gray-50 pb-4">
              <span className="flex flex-1 uppercase">{t('cart.OrderDiscount')}</span>
              <span>{basket?.c_order_discount_formatted}</span>
            </p>
          )}
          <p className="space-between flex flex-1 flex-row">
            <span className="flex flex-1 uppercase">{t('cart.SubTotal')}</span>
            <span className="flex gap-1">
              {showRegularPrice && (
                <span className="font-normal text-gray-300 line-through">{regularTotalPrice}</span>
              )}
              <span>{basket?.c_product_total_formatted}</span>
            </span>
          </p>
        </div>
        <form
          ref={formRef}
          className="w-full"
          onSubmit={checkIfLoggedIn}
          method="POST"
          action={sfcc?.basketCheckoutUrl}
        >
          <input type="hidden" name="token" value={token} />
          <input type="hidden" name="action" value="Checkout-Begin" />
          <Button variant="primary" size="l" className="ml-3" type="submit">
            {t('cart.Checkout')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
